// Create-form for triage BoardColumns. Server appends new columns to
// the end of the org's list when `order` is omitted, so this form
// doesn't surface that field — admins drag-reorder on the list page.
// (DEV-16183.)
//
// Frontend validation (DEV-16193):
//   - Name is required, trimmed, deduplicated client-side against the
//     existing column list so the user sees a clear inline error before
//     submit instead of a 409 after submit.
//   - Color: "None (neutral)" is a deliberate choice, not a missing
//     value. We do not require a non-empty color.
//   - Description is OPTIONAL. Purpose: shown as a tooltip on the
//     column header on the triage board and in the admin list — fine
//     to leave blank. Capped at 400 chars to match server-side schema.
//
// Create is ONE action (DEV-17059): "Create column" writes the row and
// lands back on the columns list, with a toast standing in for the
// confirmation the user used to get by being dropped on an edit form.
// See `-create.ts` for the why.
import { useClient } from '@cortex/react-query';
import type { BoardColumn, BoardColumnColor, IPaginated } from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState, type CSSProperties } from 'react';

import { BackButton } from '../../components/common/BackButton';
import { PageHeader } from '../../components/common/PageHeader';
import { useToast } from '../../components/common/Toast';
import { routes } from '../../lib/constants';

import { onBoardColumnCreated } from './-create';
import {
  descriptionMaxLength,
  isBoardColumnFormValid,
  nameMaxLength,
  validateBoardColumnForm,
} from './-validation';

export const Route = createFileRoute('/board-columns/new')({ component: NewBoardColumnPage });

const stack3: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' };
const fieldErrorStyle: CSSProperties = {
  color: 'var(--status-danger)',
  fontSize: 'var(--fs-xs)',
};
const counterStyle: CSSProperties = {
  fontSize: 11,
  color: 'var(--text-muted)',
  textAlign: 'right',
};

const colorOptions: Array<{ value: BoardColumnColor | ''; label: string }> = [
  { value: '', label: 'None (neutral)' },
  { value: 'teal', label: 'Teal' },
  { value: 'iris', label: 'Iris' },
  { value: 'amber', label: 'Amber' },
  { value: 'rose', label: 'Rose' },
  { value: 'mint', label: 'Mint' },
  { value: 'lavender', label: 'Lavender' },
];

function NewBoardColumnPage() {
  const client = useClient();
  const qc = useQueryClient();
  const nav = useNavigate();
  const toast = useToast();

  const [name, setName] = useState('');
  const [color, setColor] = useState<BoardColumnColor | ''>('');
  const [description, setDescription] = useState('');
  // Submit-then-validate (Formik's `submitCount > 0` pattern). The form
  // stays QUIET — no inline errors, even on blur — until the user hits
  // Submit for the first time. After that we revalidate live on every
  // change so errors clear the moment the user fixes them. Matches what
  // most form libraries default to and avoids shouting "Name is
  // required" the second the page opens.
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Fetch the org's existing columns so we can flag duplicate names
  // before the round-trip. Cached at the org level alongside the admin
  // list page query.
  const columnsQuery = useQuery({
    queryKey: ['board-columns'],
    queryFn: () => client.http.get('v1/board-columns?limit=500').json<IPaginated<BoardColumn>>(),
  });
  const existingNamesLower = useMemo(() => {
    const s = new Set<string>();
    for (const c of columnsQuery.data?.items ?? []) {
      s.add(c.name.toLowerCase());
    }
    return s;
  }, [columnsQuery.data]);

  const errors = useMemo(
    () =>
      validateBoardColumnForm({
        values: { name, color, description },
        existingNamesLower,
      }),
    [name, color, description, existingNamesLower],
  );
  const isValid = isBoardColumnFormValid(errors);

  const create = useMutation({
    mutationFn: () =>
      client.http
        .post('v1/board-columns', {
          json: {
            name: name.trim(),
            color: color === '' ? null : color,
            description: description.trim() === '' ? null : description.trim(),
          },
        })
        .json<BoardColumn>(),
    onSuccess: onBoardColumnCreated({
      invalidateColumns: () => qc.invalidateQueries({ queryKey: ['board-columns'] }),
      showToast: toast.show,
      navigate: nav,
    }),
  });

  const showNameError = submitAttempted && errors.name;
  const showDescriptionError = submitAttempted && errors.description;

  return (
    <>
      <PageHeader
        title="Create board column"
        actions={<BackButton to={routes.BOARD_COLUMNS} label="Board columns" />}
      />
      <form
        style={stack3}
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitAttempted(true);
          if (!isValid) return;
          create.mutate();
        }}
        // `noValidate` so the browser's native bubble doesn't fight our
        // inline error message. We do all the field-level work above.
        noValidate={true}
      >
        <label className="form-row">
          <span className="t-mono-label">NAME</span>
          <input
            type="text"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={nameMaxLength}
            autoFocus={true}
            placeholder="e.g. Urgent, Resolved, Needs Review"
            aria-invalid={Boolean(showNameError)}
            aria-describedby={showNameError ? 'board-column-name-error' : undefined}
          />
          {showNameError ? (
            <span id="board-column-name-error" style={fieldErrorStyle} role="alert">
              {errors.name}
            </span>
          ) : null}
        </label>
        <label className="form-row">
          <span className="t-mono-label">COLOR</span>
          <select
            className="input"
            value={color}
            onChange={(e) => setColor(e.target.value as BoardColumnColor | '')}
          >
            {colorOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <span className="t-xs t-secondary">
            Tints the column header on the triage board and the chip mid-edge in the workflow
            builder. &quot;None (neutral)&quot; leaves the column in the default surface tone.
          </span>
        </label>
        <label className="form-row">
          <span className="t-mono-label">DESCRIPTION (optional)</span>
          <textarea
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            maxLength={descriptionMaxLength}
            placeholder="What kinds of runs land here?"
            aria-invalid={Boolean(showDescriptionError)}
          />
          <div style={counterStyle}>
            {description.length} / {descriptionMaxLength}
          </div>
          <span className="t-xs t-secondary">
            Shown as a tooltip when hovering the column header on the triage board, and in the admin
            list.
          </span>
          {showDescriptionError ? (
            <span style={fieldErrorStyle} role="alert">
              {errors.description}
            </span>
          ) : null}
        </label>
        {create.error ? (
          <div className="alert alert--danger">{(create.error as Error).message}</div>
        ) : null}
        <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
          <button
            type="submit"
            className="btn btn--primary"
            // Stays clickable when invalid. The submit handler runs
            // validation and short-circuits with `setSubmitAttempted(true)`
            // — that's how the user gets their first round of error
            // messages. Disabling pre-attempt would hide the form's
            // problems entirely.
            disabled={create.isPending || (submitAttempted && !isValid)}
          >
            {create.isPending ? 'Creating…' : 'Create column'}
          </button>
        </div>
      </form>
    </>
  );
}
